import { useQuery } from '@tanstack/react-query';

import type { ResponseError } from 'Backend/common/types';
import type { operations } from 'Models/users.api.types';

import Config from 'Config';
import { getWithoutOverride } from 'Redux/api';
import { catchExpectedErrors } from '../common/catchExpectedErrors';
import { getCurrentCompanyId } from 'Lib/Utils';

type ApiOperation = operations['getUsers'];
export type ResponseStatusCode = keyof ApiOperation['responses'];
type ResponseBody<T extends ResponseStatusCode> = ApiOperation['responses'][T]['content']['application/json'];

const supportedStatusCodes = [200, 400, 403, 500] as const;

const isSupportedStatusCode = (status: number): status is (typeof supportedStatusCodes)[number] =>
    supportedStatusCodes.some((expected) => status === expected);

export type ResponseSuccess = ResponseBody<200>;
export type GetUsersResponseSuccessItems = ResponseSuccess['data'];
type Query = ApiOperation['parameters']['query'];

const getUsers = async (userStatus?: Query['status']): Promise<ResponseSuccess> => {
    const url = new URL(Config.users_v1_api);
    if (userStatus) {
        url.searchParams.append('status', userStatus);
    }
    const { status, data } = await catchExpectedErrors(() => getWithoutOverride(url, {}, null, true, true));

    /* eslint-disable no-throw-literal -- Want to throw the response object directly and pass it on to react query */

    if (!isSupportedStatusCode(status)) {
        throw {
            title: 'Unknown error',
            detail: 'Something went wrong getting companies',
        } satisfies ResponseError;
    }
    switch (status) {
        case 200:
            return data as ResponseBody<typeof status> satisfies ResponseSuccess;
        case 400:
            throw data as ResponseBody<typeof status> satisfies ResponseError;
        case 403:
            throw data as ResponseBody<typeof status> satisfies ResponseError;
        case 500:
            throw data as ResponseBody<typeof status> satisfies ResponseError;
        default:
            // Default is unreachable. Typescript gives an error if it is not.
            throw status satisfies never;
    }

    /* eslint-enable no-throw-literal -- Want to throw the response object directly and pass it on to react query */
};

/**
 * Gets all the users under the current company
 * @see accounts-management/app/src/users/api/v1/getUsers/getUsers.ts
 * */
export const useGetUsers = ({ enabled = true, status }: { enabled?: boolean; status?: Query['status'] } = {}) =>
    useQuery<ResponseSuccess, ResponseError>({
        queryKey: ['getUsers', status, getCurrentCompanyId()],
        queryFn: () => getUsers(status),
        enabled,
        refetchOnWindowFocus: false,
    });
